import styled from "styled-components";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePage } from "@inertiajs/react";
import { AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";
import { formatDate, wibDateIso, wibTime } from "./dateFormat";

// Ambient late-night date awareness for the front desk.
//
// This is the WEAKER of the two midnight guards on purpose: it fires on idle time, which has no
// relationship to the mistake — a busy desk taking a 01:00 walk-in is never idle. The read-aloud
// confirmation on the booking form is what actually catches a wrong date. This one exists so that a
// quiet desk is reminded which day it has become before the next guest walks in.
const IDLE_MS = 15 * 60 * 1000;
const TICK_MS = 1000;
const WINDOW_START_MINUTES = 23 * 60 + 59; // 23:59 WIB
const WINDOW_END_MINUTES = 6 * 60; // 06:00 WIB

const Backdrop = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1.25rem;
  position: fixed;
  z-index: 2000;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  border-top: 4px solid #8a5a00;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
  max-width: 520px;
  padding: 1.4rem 1.5rem 1.2rem;
  text-align: center;
  width: 100%;
`;

const Now = styled.div`
  color: ${theme.ink};
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.3;
`;

const Clock = styled.div`
  color: ${theme.goldDark};
  font-size: 2.6rem;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  letter-spacing: 0.02em;
  margin-top: 0.2rem;
`;

const Warning = styled.div`
  align-items: flex-start;
  background: #fdf3e3;
  border-radius: 10px;
  color: #8a5a00;
  display: flex;
  font-size: 0.92rem;
  font-weight: 700;
  gap: 0.45rem;
  line-height: 1.5;
  margin-top: 1rem;
  padding: 0.75rem 0.85rem;
  text-align: left;
`;

const Dismiss = styled.button`
  background: ${theme.gold};
  border: 0;
  border-radius: 9px;
  color: #ffffff;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 800;
  margin-top: 1.1rem;
  padding: 0.6rem 1.4rem;

  &:hover {
    background: ${theme.goldDark};
  }
`;

const minutesOfDay = (instant) => {
  const [hours, minutes] = wibTime(instant).split(":").map(Number);
  return hours * 60 + minutes;
};

// The window wraps past midnight, so it is a union of two ranges rather than one comparison.
const inNightWindow = (instant) => {
  const minutes = minutesOfDay(instant);
  return minutes >= WINDOW_START_MINUTES || minutes < WINDOW_END_MINUTES;
};

const NightDateGuard = () => {
  const { t, lang } = useT();
  const page = usePage();
  const roles = page.props.current_user?.roles || [];
  const isStaffFo = roles.includes("staff_fo");

  const [now, setNow] = useState(() => new Date());
  const [visible, setVisible] = useState(false);
  // Seeded on mount rather than at render, so the idle clock starts when the shell actually mounts.
  const lastActivityRef = useRef(0);

  const markActive = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!isStaffFo) return undefined;

    markActive();
    const events = [
      "pointerdown",
      "keydown",
      "wheel",
      "touchstart",
      "visibilitychange",
    ];
    events.forEach((event) =>
      window.addEventListener(event, markActive, { passive: true }),
    );
    return () =>
      events.forEach((event) => window.removeEventListener(event, markActive));
  }, [isStaffFo, markActive]);

  useEffect(() => {
    if (!isStaffFo) return undefined;

    const id = setInterval(() => {
      const instant = new Date();
      setNow(instant);
      if (!inNightWindow(instant)) {
        setVisible(false);
        return;
      }
      if (Date.now() - lastActivityRef.current >= IDLE_MS) setVisible(true);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [isStaffFo]);

  if (!isStaffFo || !visible) return null;

  const dismiss = () => {
    markActive();
    setVisible(false);
  };

  return (
    <Backdrop>
      <Card>
        <Now>{formatDate(wibDateIso(now), lang, false)}</Now>
        <Clock>{wibTime(now)} WIB</Clock>
        <Warning>
          <AlertTriangle size={18} style={{ flex: "0 0 auto" }} />
          <span>
            {t("night_guard_warning").replace(
              "%{date}",
              formatDate(wibDateIso(now), lang, false),
            )}
          </span>
        </Warning>
        <Dismiss type="button" onClick={dismiss}>
          {t("night_guard_ok")}
        </Dismiss>
      </Card>
    </Backdrop>
  );
};

export default NightDateGuard;
